// packages/console/src/components/PasswordLoginForm.tsx
//
// Email + password sign-in for collaborators provisioned by a workspace owner
// (no Google account). On success the tenancy sets the refresh cookie and the
// caller boots the session (onSuccess -> session.refresh), the same path OIDC
// uses. A generic error message never reveals whether an account exists.

import { useState, type CSSProperties, type FormEvent } from "react";
import { passwordLogin } from "../api/tenancy";

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 11px",
  borderRadius: "var(--r-sm)",
  border: "1px solid var(--line-strong)",
  background: "var(--bg-input)",
  color: "var(--fg1)",
  font: "400 13px var(--font-sans)",
};

export function PasswordLoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !email.trim() || !password) return;
    setBusy(true);
    setErr(null);
    const res = await passwordLogin(email.trim(), password);
    if (res.ok) {
      onSuccess();
      return; // session boot swaps this view out; leave busy set
    }
    setBusy(false);
    setErr(res.message);
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 8, width: 264 }}>
      <input
        type="email"
        placeholder="Email"
        value={email}
        autoComplete="username"
        spellCheck={false}
        onChange={(e) => setEmail(e.target.value)}
        style={inputStyle}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        autoComplete="current-password"
        onChange={(e) => setPassword(e.target.value)}
        style={inputStyle}
      />
      {err && <span style={{ font: "var(--t-micro)", color: "var(--err)" }}>{err}</span>}
      <button
        type="submit"
        className="ae-b"
        disabled={busy || !email.trim() || !password}
        style={{
          font: "var(--t-btn)",
          letterSpacing: "var(--ls-btn)",
          textTransform: "uppercase",
          color: "#fff",
          background: "var(--blue-500)",
          border: "1px solid transparent",
          borderRadius: "var(--r-sm)",
          padding: "10px 18px",
          cursor: busy || !email.trim() || !password ? "not-allowed" : "pointer",
          opacity: busy || !email.trim() || !password ? 0.6 : 1,
        }}
      >
        {busy ? "Signing in..." : "Sign in with password"}
      </button>
    </form>
  );
}
